import React from "react";
import { Box, Modal } from "@mui/material";
import Slide from "@mui/material/Slide";
import CircularProgress from "@mui/material/CircularProgress";

const overlayStyles = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100vw",
  height: "100vh",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1300,
};

const myStyles = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: "640px",
  background: "var(--qv-bg-ffffff, #fff)",
  borderRadius: "12px",
  padding: "0",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  maxHeight: "85vh",
  overflow: "hidden",
};

const scrollContent = { padding: "20px", overflowY: "auto", flex: 1 };
const footer = {
  padding: "15px 20px",
  borderTop: "1px solid #ddd",
  background: "var(--qv-bg-ffffff, #fff)",
  bottom: 0,
  borderBottomLeftRadius: "12px",
  borderBottomRightRadius: "12px",
};
const card = { marginTop: "15px", padding: "14px", background: "var(--qv-bg-f5f5f5, #f5f5f5)", borderRadius: "8px" };
const reasonRow = { display: "flex", justifyContent: "space-between", fontSize: "0.9em", color: "var(--qv-tx-555555, #555)", marginTop: "4px" };

const formatReason = (raw) => {
  if (!raw || typeof raw !== "string") return "";
  return raw
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatTimestamp = (ts) => {
  if (!ts) return "";
  const d = new Date(ts.includes("T") || ts.includes("Z") ? ts : ts.replace(" ", "T") + "Z");
  if (isNaN(d.getTime())) return ts;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const protocolStyles = {
  restock_and_refund: { bg: "var(--qv-bg-fff4e5, #fff4e5)", border: "var(--qv-bd-ff9800, #ff9800)", label: "Restock & Refund" },
  redeliver_or_cancel: { bg: "var(--qv-bg-e3f2fd, #e3f2fd)", border: "var(--qv-bd-1976d2, #1976d2)", label: "Attempt Redelivery vs Cancel" },
  merchant_resolve: { bg: "var(--qv-bg-fff8e1, #fff8e1)", border: "var(--qv-bd-fbc02d, #fbc02d)", label: "Contact Support" },
  generic: { bg: "var(--qv-bg-f5f5f5, #f5f5f5)", border: "var(--qv-bd-9e9e9e, #9e9e9e)", label: "Review with Support" },
  none: { bg: "var(--qv-bg-e8f5e9, #e8f5e9)", border: "var(--qv-bd-43a047, #43a047)", label: "No Action Needed" },
};

const ResolutionActionModal = ({ open, onClose, isLoading, payload, onContactSupport }) => {
  const data = payload?.data || {};
  const hasFailure = payload?.has_failure === true;
  const protocolKey = data.protocol_key || "none";
  const style = protocolStyles[protocolKey] || protocolStyles.generic;
  const instructions = Array.isArray(data.instructions) ? data.instructions : [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-describedby="resolution-action-modal"
      className="delete-modal-outer-div"
      closeAfterTransition
    >
      <Slide direction="down" in={open} mountOnEnter unmountOnExit>
        <Box style={overlayStyles}>
          <Box className="delete-Box-modal qv-order-modal-panel" style={myStyles}>
            <div style={scrollContent}>
              <h3 style={{ marginBottom: "12px", fontWeight: 500 }}>Resolution Action Center</h3>

              {isLoading ? (
                <Box style={{ display: "flex", justifyContent: "center", padding: "30px" }}>
                  <CircularProgress sx={{ color: "var(--qv-tx-000000, #000000) !important" }} />
                </Box>
              ) : !hasFailure ? (
                <div style={{ ...card, background: protocolStyles.none.bg, borderLeft: `4px solid ${protocolStyles.none.border}` }}>
                  <p style={{ fontWeight: 500, marginBottom: "6px" }}>No resolution needed</p>
                  <p style={{ color: "var(--qv-tx-444444, #444)" }}>
                    {payload?.message || "This delivery has no failure or cancellation that requires action."}
                  </p>
                  {data.delivery_status && (
                    <p style={reasonRow}>
                      <span>Delivery status</span>
                      <span style={{ textTransform: "capitalize" }}>
                        {(data.delivery_status || "").replaceAll("_", " ")}
                      </span>
                    </p>
                  )}
                </div>
              ) : (
                <>
                  <div style={{ ...card, background: "var(--qv-bg-fdecea, #fdecea)", borderLeft: "4px solid var(--qv-bd-d32f2f, #d32f2f)", marginTop: 0 }}>
                    <p style={{ fontSize: "0.8em", color: "var(--qv-tx-666666, #666)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      {data.reason_source === "return" ? "Return Reason" : "Cancellation Reason"}
                    </p>
                    <p style={{ fontWeight: 500, fontSize: "1.05em", marginTop: "4px", color: "var(--qv-tx-b71c1c, #b71c1c)", wordBreak: "break-word" }}>
                      {formatReason(data.reason) || "Not provided by Uber"}
                    </p>
                    <div style={{ fontSize: "0.85em", color: "var(--qv-tx-555555, #555)", marginTop: "6px", display: "flex", flexWrap: "wrap", gap: "12px" }}>
                      {data.cancelled_by && (
                        <span>
                          {data.reason_source === "return" ? "Returned by " : "Cancelled by "}
                          <strong style={{ textTransform: "capitalize" }}>{data.cancelled_by}</strong>
                        </span>
                      )}
                      {data.failed_at && (
                        <span>
                          {data.reason_source === "return" ? "Returned at " : "Cancelled at "}
                          <strong>{formatTimestamp(data.failed_at)}</strong>
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={{ ...card, background: style.bg, borderLeft: `4px solid ${style.border}` }}>
                    <p style={{ fontSize: "0.8em", color: "var(--qv-tx-666666, #666)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Recommended Action
                    </p>
                    <p style={{ fontWeight: 500, fontSize: "1.1em", marginTop: "4px" }}>{data.title || style.label}</p>
                  </div>

                  <div style={card}>
                    <p style={{ fontWeight: 500, marginBottom: "10px" }}>Steps to take</p>
                    {instructions.length > 0 ? (
                      <ol style={{ paddingLeft: "20px", lineHeight: "1.6" }}>
                        {instructions.map((step, idx) => (
                          <li key={idx} style={{ marginBottom: "6px" }}>
                            {step}
                          </li>
                        ))}
                      </ol>
                    ) : (
                      <p style={{ color: "var(--qv-tx-777777, #777)" }}>No specific steps available — contact Quickvee Support for guidance.</p>
                    )}
                  </div>

                  <div style={card}>
                    <p style={{ fontWeight: 500, marginBottom: "8px" }}>Details</p>
                    {data.delivery_status && (
                      <div style={reasonRow}>
                        <span>Delivery status</span>
                        <span style={{ fontWeight: 500, textTransform: "capitalize" }}>
                          {(data.delivery_status || "").replaceAll("_", " ")}
                        </span>
                      </div>
                    )}
                    {data.delivery_id && (
                      <div style={reasonRow}>
                        <span>Delivery ID</span>
                        <span style={{ fontWeight: 500 }}>{data.delivery_id}</span>
                      </div>
                    )}
                    {data.tracking_url && (
                      <div style={{ marginTop: "8px" }}>
                        <a
                          href={data.tracking_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "var(--qv-brand-text, #0a64f9)" }}
                        >
                          Open Uber tracking page
                        </a>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <div style={{ ...footer, display: "flex", gap: "10px" }}>
              {hasFailure && typeof onContactSupport === "function" && (
                <button
                  onClick={onContactSupport}
                  style={{
                    background: "var(--qv-bg-ffffff, #fff)",
                    color: "var(--qv-brand-text, #0a64f9)",
                    border: "1px solid var(--qv-brand, #0a64f9)",
                    flex: 1,
                    padding: "10px 0",
                    borderRadius: "6px",
                    fontWeight: 500,
                  }}
                >
                  Contact Quickvee Support
                </button>
              )}
              <button
                onClick={onClose}
                style={{
                  background: "var(--qv-brand, #0a64f9)",
                  color: "#fff",
                  flex: hasFailure ? 1 : "1 1 100%",
                  padding: "10px 0",
                  borderRadius: "6px",
                  fontWeight: 500,
                }}
              >
                Close
              </button>
            </div>
          </Box>
        </Box>
      </Slide>
    </Modal>
  );
};

export default ResolutionActionModal;
